import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Charts
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function EstadoResultados() {
  const [fechaInicio, setFechaInicio] = useState("2024-11-01");
  const [fechaFin, setFechaFin] = useState("2024-11-30");

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    ingresos: [],
    costoVentas: [],
    gastosOperacion: { ventas: [], administracion: [] },
    otrosGastosIngresos: { ingresos: [], gastos: [] },
    impuestos: { cuenta: "ISR", monto: 0 },
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(API_ENDPOINTS.CONTABILIDAD.ESTADO_RESULTADOS, {
          params: {
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
          },
        });
        setData(response.data);
      } catch (error) {
        console.error("Error al cargar estado de resultados:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fechaInicio, fechaFin]);

  const { ingresos, costoVentas, gastosOperacion, otrosGastosIngresos, impuestos } = data;

  // Cálculos
  const totalIngresos = ingresos.reduce((sum, item) => sum + item.monto, 0);
  const totalCostoVentas = costoVentas.reduce((sum, item) => sum + item.monto, 0);
  const utilidadBruta = totalIngresos - totalCostoVentas;
  const margenBruto = totalIngresos > 0 ? (utilidadBruta / totalIngresos) * 100 : 0;

  const totalGastosVentas = gastosOperacion.ventas.reduce((sum, item) => sum + item.monto, 0);
  const totalGastosAdmin = gastosOperacion.administracion.reduce(
    (sum, item) => sum + item.monto,
    0
  );
  const totalGastosOperacion = totalGastosVentas + totalGastosAdmin;

  const utilidadOperacion = utilidadBruta - totalGastosOperacion;
  const margenOperacion = totalIngresos > 0 ? (utilidadOperacion / totalIngresos) * 100 : 0;

  const totalOtrosIngresos = otrosGastosIngresos.ingresos.reduce(
    (sum, item) => sum + item.monto,
    0
  );
  const totalOtrosGastos = otrosGastosIngresos.gastos.reduce((sum, item) => sum + item.monto, 0);

  const utilidadAntesImpuestos = utilidadOperacion + totalOtrosIngresos - totalOtrosGastos;
  const utilidadNeta = utilidadAntesImpuestos - impuestos.monto;
  const margenNeto = totalIngresos > 0 ? (utilidadNeta / totalIngresos) * 100 : 0;

  const LineaItem = ({ item, nivel = 0 }) => (
    <MDBox display="flex" justifyContent="space-between" py={0.5} px={3} ml={nivel * 2}>
      <MDTypography variant="button" fontWeight="regular">
        {item.cuenta}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium">
        {formatCurrency(item.monto)}
      </MDTypography>
    </MDBox>
  );

  LineaItem.propTypes = {
    item: PropTypes.shape({
      cuenta: PropTypes.string.isRequired,
      monto: PropTypes.number.isRequired,
    }).isRequired,
    nivel: PropTypes.number,
  };

  const SubTotal = ({ label, monto, color = "text", nivel = 0 }) => (
    <MDBox
      display="flex"
      justifyContent="space-between"
      py={1}
      px={3}
      ml={nivel * 2}
      sx={{ bgcolor: "grey.100" }}
    >
      <MDTypography variant="button" fontWeight="bold" color={color}>
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="bold" color={color}>
        {formatCurrency(monto)}
      </MDTypography>
    </MDBox>
  );

  SubTotal.propTypes = {
    label: PropTypes.string.isRequired,
    monto: PropTypes.number.isRequired,
    color: PropTypes.string,
    nivel: PropTypes.number,
  };

  const Total = ({ label, monto, color = "info" }) => (
    <MDBox
      display="flex"
      justifyContent="space-between"
      py={1.5}
      px={3}
      sx={{ bgcolor: `${color}.main` }}
    >
      <MDTypography variant="h6" fontWeight="bold" color="white">
        {label}
      </MDTypography>
      <MDTypography variant="h6" fontWeight="bold" color="white">
        {formatCurrency(monto)}
      </MDTypography>
    </MDBox>
  );

  Total.propTypes = {
    label: PropTypes.string.isRequired,
    monto: PropTypes.number.isRequired,
    color: PropTypes.string,
  };

  // Datos para la gráfica
  const chartData = {
    labels: ["Ingresos", "Costo Ventas", "Gastos Operación", "Utilidad Neta"],
    datasets: [
      {
        label: "Monto (MXN)",
        data: [totalIngresos, totalCostoVentas, totalGastosOperacion, utilidadNeta],
        backgroundColor: [
          "rgba(75, 192, 192, 0.7)",
          "rgba(255, 99, 132, 0.7)",
          "rgba(255, 206, 86, 0.7)",
          "rgba(54, 162, 235, 0.7)",
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: "Composición del Estado de Resultados" },
    },
  };

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Estado de Resultados (P&L)
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <TextField
                    type="date"
                    label="Fecha Inicio"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="Fecha Fin"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <MDButton variant="gradient" color="info">
                    <Icon>print</Icon>&nbsp;Imprimir
                  </MDButton>
                </MDBox>
              </MDBox>

              <Grid container spacing={3}>
                {/* Estado de Resultados */}
                <Grid item xs={12} md={8}>
                  <Card variant="outlined">
                    {/* Ingresos */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="success">
                          INGRESOS
                        </MDTypography>
                      </MDBox>
                      {ingresos.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={1} />
                      ))}
                      <Total label="TOTAL INGRESOS" monto={totalIngresos} color="success" />
                    </MDBox>

                    <Divider />

                    {/* Costo de Ventas */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="error">
                          COSTO DE VENTAS
                        </MDTypography>
                      </MDBox>
                      {costoVentas.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={1} />
                      ))}
                      <SubTotal
                        label="Total Costo de Ventas"
                        monto={totalCostoVentas}
                        color="error"
                      />
                    </MDBox>

                    <Total label="UTILIDAD BRUTA" monto={utilidadBruta} color="info" />

                    <Divider />

                    {/* Gastos de Operación */}
                    <MDBox py={2}>
                      <MDBox px={3} mb={1}>
                        <MDTypography variant="button" fontWeight="bold" color="warning">
                          GASTOS DE OPERACIÓN
                        </MDTypography>
                      </MDBox>

                      <MDBox px={3} mt={1} mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          Gastos de Ventas
                        </MDTypography>
                      </MDBox>
                      {gastosOperacion.ventas.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={2} />
                      ))}
                      <SubTotal
                        label="Total Gastos de Ventas"
                        monto={totalGastosVentas}
                        nivel={1}
                      />

                      <MDBox px={3} mt={2} mb={0.5}>
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          Gastos de Administración
                        </MDTypography>
                      </MDBox>
                      {gastosOperacion.administracion.map((item, index) => (
                        <LineaItem key={index} item={item} nivel={2} />
                      ))}
                      <SubTotal
                        label="Total Gastos de Administración"
                        monto={totalGastosAdmin}
                        nivel={1}
                      />
                      <SubTotal
                        label="TOTAL GASTOS DE OPERACIÓN"
                        monto={totalGastosOperacion}
                        color="warning"
                      />
                    </MDBox>

                    <Total
                      label="UTILIDAD DE OPERACIÓN"
                      monto={utilidadOperacion}
                      color="primary"
                    />

                    <Divider />

                    {/* Otros Ingresos y Gastos */}
                    <MDBox py={2}>
                      {otrosGastosIngresos.ingresos.map((item, index) => (
                        <LineaItem key={index} item={{ ...item, cuenta: `(+) ${item.cuenta}` }} />
                      ))}
                      {otrosGastosIngresos.gastos.map((item, index) => (
                        <LineaItem key={index} item={{ ...item, cuenta: `(-) ${item.cuenta}` }} />
                      ))}
                    </MDBox>

                    <SubTotal
                      label="UTILIDAD ANTES DE IMPUESTOS"
                      monto={utilidadAntesImpuestos}
                      color="info"
                    />

                    <Divider />

                    {/* Impuestos */}
                    <MDBox py={2}>
                      <LineaItem item={impuestos} />
                    </MDBox>

                    <Total
                      label="UTILIDAD NETA"
                      monto={utilidadNeta}
                      color={utilidadNeta >= 0 ? "success" : "error"}
                    />
                  </Card>
                </Grid>

                {/* Análisis y Gráfica */}
                <Grid item xs={12} md={4}>
                  {/* Indicadores */}
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <MDBox p={2}>
                      <MDTypography variant="h6" fontWeight="medium" mb={2}>
                        Indicadores
                      </MDTypography>
                      <MDBox mb={2}>
                        <MDTypography variant="caption" color="text">
                          Margen Bruto
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="bold" color="success">
                          {margenBruto.toFixed(2)}%
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={2}>
                        <MDTypography variant="caption" color="text">
                          Margen Operación
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="bold" color="info">
                          {margenOperacion.toFixed(2)}%
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="caption" color="text">
                          Margen Neto
                        </MDTypography>
                        <MDTypography variant="h5" fontWeight="bold" color="primary">
                          {margenNeto.toFixed(2)}%
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Card>

                  {/* Gráfica */}
                  <Card variant="outlined">
                    <MDBox p={2}>
                      <MDBox height="250px">
                        <Bar data={chartData} options={chartOptions} />
                      </MDBox>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
